import { useTranslation } from '@rhino-project/core/i18n';
import { ReactNode, useMemo } from 'react';
import {
  useGlobalComponentForModel,
  useModelContext
} from '@rhino-project/core/hooks';
import { BreadcrumbsItem, Breadcrumbs, BreadcrumbsProps } from '@heroui/react';
import { RhinoRecord } from '@rhino-project/core';
import { useBaseOwnerPath } from '../../hooks';

export type ModelBreadcrumbProps = {
  lastItem?: ReactNode;
  resource?: RhinoRecord;
} & BreadcrumbsProps;

export const ModelBreadcrumbBase = ({
  lastItem,
  resource,
  ...props
}: ModelBreadcrumbProps) => {
  const { t } = useTranslation();
  const { model } = useModelContext() as {
    model: { name: string; model: string; pluralReadableName: string };
  };
  const { build } = useBaseOwnerPath();

  const breadcrumbs = useMemo(() => {
    const result = [];

    if (resource) {
      result.unshift({
        key: `${model.name}-${String(resource.id)}`,
        children: resource?.display_name as ReactNode,
        href: build(`${model.model}/${resource.id}`)
      });
    }

    result.unshift({
      key: model.name,
      children: t(`rhino.generated.${model.model}.other`, {
        defaultValue: model.pluralReadableName
      }),
      href: build(`${model.model}`)
    });

    if (lastItem) {
      result.push({
        key: 'lastItem',
        children: lastItem
      });
    }

    return result;
  }, [
    build,
    lastItem,
    model.model,
    model.name,
    model.pluralReadableName,
    resource,
    t
  ]);

  return (
    <Breadcrumbs {...props}>
      {breadcrumbs.map((breadcrumb) => (
        // eslint-disable-next-line @eslint-react/no-missing-key
        <BreadcrumbsItem {...breadcrumb} />
      ))}
    </Breadcrumbs>
  );
};

export const ModelBreadcrumb = (props: ModelBreadcrumbProps) =>
  useGlobalComponentForModel('ModelBreadcrumb', ModelBreadcrumbBase, props);
